import React, { useEffect, useState, useCallback, useRef } from "react";
import "./DatabaseTablesPage.css";
import { API_BASE } from "../api/config";
import { SpaltenPanel, StrukturSeitenleiste, tabelleLoeschen } from "./dbtabellen/StrukturWerkzeuge";
import { ExcelAbgleich, VbaDialog } from "./dbtabellen/ExcelAbgleich";

/**
 * Welle 97: Die Seite läuft gegen zwei Datenbanken.
 *   apiBase="/database"   → alte DB imho (Standard, unverändert)
 *   apiBase="/database/v2"  → neue DB imho_v2, mit Struktur-Werkzeugen (mitStruktur)
 * Der Excel-/Dropbox-Abgleich (mitSync) gibt es nur für die alte DB.
 */
// Welle 123: Filter „(leer)" für NULL/leere Zellen, „nur aktive" für Tabellen mit Schließ-/Kündigungsspalte
const LEER_TOKEN = ["(leer)", "∅", "leer", "null"];
const AKTIV_SPALTEN = ["geschlossen_am", "x_gekuendigt"];
const istLeer = (v) => v === null || v === undefined || String(v).trim() === "";

const DatabaseTablesPage = ({ apiBase = "/database", titel = "📊 Alle Datenbank-Tabellen", mitSync = true, mitStruktur = false }) => {
  const B = `${API_BASE}${apiBase}`;
  const [spaltenPanel, setSpaltenPanel] = useState(false);
  // Welle 118: Daten.xlsm abgleichen + Live-Modul (VBA) je Laschen-Tabelle — nur DB-Tabellen 2
  const [abgleichOffen, setAbgleichOffen] = useState(false);
  const [vbaTabelle, setVbaTabelle] = useState(null);
  // Welle 97: Spaltengruppen („[Miete] …" im Spaltenkommentar) — Band über den Spalten, einklappbar
  const [zuGruppen, setZuGruppen] = useState(() => new Set());
  const GRUPPEN_FARBEN = ["#3b7dd8", "#2e9e5b", "#c2640a", "#8b5cf6", "#d9465f", "#0e9aa7", "#a3a019", "#6b7280"];
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [excelCode, setExcelCode] = useState(null);
  const [showExcelPopup, setShowExcelPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Tabellen-Vorschau: Suche, Filter, Paginierung, Bearbeiten
  const [rowSearch, setRowSearch] = useState("");
  const [colFilters, setColFilters] = useState({});       // { colName: filterText } — „(leer)" / „!(leer)" = leer / gefüllt
  const [sortierung, setSortierung] = useState(null);     // Welle 123: { col, ab } — Klick auf die Überschrift
  const [nurAktive, setNurAktive] = useState(true);       // Welle 123: geschlossene/gekündigte ausblenden
  const [showAllRows, setShowAllRows] = useState(false);
  const [editingCell, setEditingCell] = useState(null);   // { rowIdx, col }
  const [editingValue, setEditingValue] = useState("");
  const [editRow, setEditRow] = useState(null);           // rowIdx der aktuell zu bearbeitenden Zeile
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [blobUploading, setBlobUploading] = useState(null); // { rowIdx, col } während Upload

  // Insert neuer Zeile
  const [showInsert, setShowInsert] = useState(false);
  const [insertData, setInsertData] = useState({});
  const [inserting, setInserting] = useState(false);

  // Welle 46.G: echte Spalten-Metadaten (PK/auto_increment) + Referenz-Werte
  const [columnsMeta, setColumnsMeta] = useState({});   // { field: {type, key, extra, null} }
  const [refValues, setRefValues] = useState({});       // { spalte: {ziel, werte:[{id,label}]} }
  const [editRowPk, setEditRowPk] = useState(null);     // Original-PK der Zeile im Edit-Modus

  // Welle 46.H: Excel-Roundtrip je Tabelle (runterladen → bearbeiten → hochladen)

  // Welle 47: Google-Sheet-Verknüpfung je Tabelle
  const [sheetInfo, setSheetInfo] = useState(null);       // {verknuepft, sheet}
  const [sheetPanelOpen, setSheetPanelOpen] = useState(false);
  const [sheetUrlInput, setSheetUrlInput] = useState("");
  const [sheetBusy, setSheetBusy] = useState(false);
  // Welle 49: übersprungene Zeilen des letzten Auto-Syncs
  const [sheetFehler, setSheetFehler] = useState([]);

  // Spalten-Filter State
  const [popupColumns, setPopupColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnsExpanded, setColumnsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // === Sync State ===
  const [syncConfigs, setSyncConfigs] = useState({});       // {table_name: {writeable, pk, ...}}
  const [syncDragging, setSyncDragging] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);       // Analyse-Ergebnis
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [diffActiveTab, setDiffActiveTab] = useState(0);
  const [diffChecked, setDiffChecked] = useState({});       // {tabIdx: {added: Set, modified: Set, deleted: Set}}
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [dropboxFiles, setDropboxFiles] = useState(null);
  const [dropboxLoading, setDropboxLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Gruppen-State
  const [groups, setGroups] = useState({});              // { alias: label }
  const [groupOrder, setGroupOrder] = useState([]);      // [alias] — Reihenfolge aus DB
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // Welle 22.F: verschachtelte Untertabellen + Verknüpfungs-Buttons
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [verknuepfungen, setVerknuepfungen] = useState([]);
  // Welle 21.D: Drag & Drop Tabelle → Sektion + neue Sektion anlegen
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [newSectionAlias, setNewSectionAlias] = useState("");
  const [newSectionLabel, setNewSectionLabel] = useState("");

  useEffect(() => {
    fetchTables();
    fetchSyncConfigs();
  }, []);

  // === Sync Config ===
  const fetchSyncConfigs = async () => {
    if (!mitSync) return;
    try {
      const resp = await fetch(`${API_BASE}/database/sync/config`);
      if (!resp.ok) return;
      const data = await resp.json();
      const map = {};
      (data.configs || []).forEach(c => { map[c.table_name] = c; });
      setSyncConfigs(map);
    } catch { /* ignore */ }
  };

  const toggleWriteable = async (tableName) => {
    const current = syncConfigs[tableName];
    const newWriteable = !(current?.writeable);
    try {
      await fetch(`${API_BASE}/database/sync/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table_name: tableName,
          writeable: newWriteable,
          primary_key_column: current?.primary_key_column || "id",
          protected_columns: current?.protected_columns || [],
        }),
      });
      setSyncConfigs(prev => ({
        ...prev,
        [tableName]: { ...prev[tableName], table_name: tableName, writeable: newWriteable },
      }));
    } catch (e) {
      console.error("toggleWriteable error:", e);
    }
  };

  // === Sync Upload ===
  const handleSyncUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith(".xlsx")) {
      alert("Bitte eine .xlsx Datei wählen");
      return;
    }
    setSyncLoading(true);
    setSyncResult(null);
    setApplyResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await fetch(`${API_BASE}/database/sync/upload`, { method: "POST", body: formData });
      if (!resp.ok) throw new Error("Upload fehlgeschlagen");
      const data = await resp.json();
      setSyncResult(data);
      initDiffChecked(data.tables || []);
      setDiffActiveTab(0);
      setShowDiffModal(true);
    } catch (e) {
      alert("❌ Sync-Fehler: " + e.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDropboxDiff = async (filePath) => {
    setSyncLoading(true);
    setSyncResult(null);
    setApplyResult(null);
    try {
      const resp = await fetch(`${API_BASE}/database/sync/dropbox/diff?file_path=${encodeURIComponent(filePath)}`, { method: "POST" });
      if (!resp.ok) throw new Error("Dropbox-Diff fehlgeschlagen");
      const data = await resp.json();
      setSyncResult({ ok: true, file_name: filePath.split("/").pop(), tables: data.tables });
      initDiffChecked(data.tables || []);
      setDiffActiveTab(0);
      setShowDiffModal(true);
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const initDiffChecked = (tableDiffs) => {
    const checked = {};
    tableDiffs.forEach((t, idx) => {
      if (!t.diff) { checked[idx] = { added: new Set(), modified: new Set(), deleted: new Set() }; return; }
      checked[idx] = {
        added: new Set(t.diff.added.map((_, i) => i)),
        modified: new Set(t.diff.modified.map((_, i) => i)),
        deleted: new Set(),  // Löschungen default aus
      };
    });
    setDiffChecked(checked);
  };

  const toggleDiffRow = (tabIdx, section, rowIdx) => {
    setDiffChecked(prev => {
      const tab = { ...prev[tabIdx] };
      const s = new Set(tab[section]);
      if (s.has(rowIdx)) s.delete(rowIdx); else s.add(rowIdx);
      tab[section] = s;
      return { ...prev, [tabIdx]: tab };
    });
  };

  // === Apply ===
  const handleApply = async () => {
    if (!syncResult?.tables) return;
    setApplyLoading(true);
    setApplyResult(null);

    const applyTables = [];
    syncResult.tables.forEach((t, idx) => {
      if (!t.diff || !t.writeable) return;
      const ch = diffChecked[idx];
      if (!ch) return;
      const added = t.diff.added.filter((_, i) => ch.added.has(i)).map(r => r.data);
      const modified = t.diff.modified.filter((_, i) => ch.modified.has(i));
      const deleted = t.diff.deleted.filter((_, i) => ch.deleted.has(i)).map(r => r.pk);
      if (added.length === 0 && modified.length === 0 && deleted.length === 0) return;
      applyTables.push({ table_name: t.db_table, added, modified, deleted });
    });

    if (applyTables.length === 0) {
      alert("Keine Änderungen ausgewählt");
      setApplyLoading(false);
      return;
    }

    try {
      const resp = await fetch(`${API_BASE}/database/sync/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tables: applyTables }),
      });
      if (!resp.ok) throw new Error("Apply fehlgeschlagen");
      const data = await resp.json();
      setApplyResult(data);
      fetchTables(); // Refresh table counts
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setApplyLoading(false);
    }
  };

  // === Dropbox ===
  const fetchDropboxFiles = async () => {
    setDropboxLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/database/sync/dropbox/files`);
      if (!resp.ok) throw new Error("Dropbox-Fehler");
      const data = await resp.json();
      setDropboxFiles(data);
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setDropboxLoading(false);
    }
  };

  // === Drag & Drop ===
  const handleDragOver = (e) => { e.preventDefault(); setSyncDragging(true); };
  const handleDragLeave = () => { setSyncDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setSyncDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleSyncUpload(file);
  };
  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleSyncUpload(file);
    e.target.value = "";
  };

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${B}/tables`);
      if (!response.ok) throw new Error("Fehler beim Abrufen der Tabellen");
      const data = await response.json();
      setTables(data.tables);
      if (data.groups) {
        setGroups(data.groups);
        setGroupOrder(data.group_order || Object.keys(data.groups));
        // Alle Gruppen initial eingeklappt
        setCollapsedGroups(new Set([...Object.keys(data.groups), "_other"]));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (alias) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias); else next.add(alias);
      return next;
    });
  };

  // Tabellen nach Gruppen + "Sonstige" aufteilen.
  // Reihenfolge kommt aus der DB (group_order); leere Gruppen bleiben sichtbar
  // (außer bei aktiver Suche), damit man Tabellen hineinziehen kann.
  // Welle 22.F: Untertabellen (t.parent) hängen verschachtelt unter ihrer
  // Haupttabelle und tauchen nicht mehr flach in der Liste auf.
  const getGroupedTables = () => {
    const q = searchTerm.toLowerCase();
    const passt = (t) => t.name.toLowerCase().includes(q);
    const kinderVon = {};
    for (const t of tables) {
      if (t.parent) {
        if (!kinderVon[t.parent]) kinderVon[t.parent] = [];
        kinderVon[t.parent].push(t);
      }
    }
    const order = groupOrder.length ? groupOrder : Object.keys(groups);
    const grouped = {};
    const ungrouped = [];
    for (const t of tables) {
      if (t.parent && tables.some(p => p.name === t.parent)) continue; // hängt unter Haupttabelle
      const kinder = (kinderVon[t.name] || []).filter(k => !searchTerm || passt(k) || passt(t));
      if (searchTerm && !passt(t) && kinder.length === 0) continue;
      const eintrag = { ...t, kinder };
      if (t.group && groups[t.group]) {
        if (!grouped[t.group]) grouped[t.group] = [];
        grouped[t.group].push(eintrag);
      } else {
        ungrouped.push(eintrag);
      }
    }
    const result = [];
    for (const alias of order) {
      const grpTables = grouped[alias] || [];
      if (grpTables.length > 0 || !searchTerm) {
        result.push({ alias, label: groups[alias], tables: grpTables });
      }
    }
    if (ungrouped.length > 0 || !searchTerm) {
      result.push({ alias: "_other", label: "Sonstige", tables: ungrouped });
    }
    return result;
  };

  const toggleParent = (name) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  // === Welle 21.D: Drag & Drop Tabelle → Sektion ===
  const handleTableDragStart = (e, name) => {
    e.dataTransfer.setData("text/plain", name);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleGroupDrop = async (e, alias) => {
    e.preventDefault();
    setDragOverGroup(null);
    const name = e.dataTransfer.getData("text/plain");
    if (!name) return;
    const gruppe = alias === "_other" ? null : alias;
    const vorher = tables.find(t => t.name === name)?.group ?? null;
    if (vorher === gruppe) return;
    // Optimistisch verschieben, bei Fehler neu laden
    setTables(prev => prev.map(t => (t.name === name ? { ...t, group: gruppe } : t)));
    try {
      const resp = await fetch(`${B}/tables/gruppen/zuordnung`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tabelle: name, gruppe }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || "Zuordnung fehlgeschlagen");
      }
    } catch (err) {
      setError(`Verschieben von '${name}' fehlgeschlagen: ${err.message}`);
      fetchTables();
    }
  };

  const createSection = async () => {
    const alias = newSectionAlias.trim().toUpperCase();
    const label = newSectionLabel.trim();
    if (!alias || !label) return;
    try {
      const resp = await fetch(`${B}/tables/gruppen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ alias, label }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(d.detail || "Anlegen fehlgeschlagen");
      setGroups(prev => ({ ...prev, [d.alias]: d.label }));
      setGroupOrder(prev => [...prev, d.alias]);
      setNewSectionOpen(false);
      setNewSectionAlias("");
      setNewSectionLabel("");
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchTableData = async (tableName) => {
    try {
      setTableLoading(true);
      setSelectedTable(tableName);
      setRowSearch("");
      setColFilters({});
      setSortierung(null);
      setShowAllRows(false);
      setEditRow(null);
      setEditingCell(null);
      setSaveMsg(null);
      const response = await fetch(`${B}/table/${tableName}?limit=50000`);
      if (!response.ok) throw new Error("Fehler beim Abrufen der Tabellendaten");
      const data = await response.json();
      setTableData(data);
      // Welle 46.G: Spalten-Metadaten (echter PK statt „erste Spalte") +
      // gültige Referenz-Werte (Dropdowns) — beides nicht-fatal nachladen
      setColumnsMeta({});
      setRefValues({});
      fetch(`${B}/table/${tableName}/columns`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          if (d?.columns) {
            const meta = {};
            d.columns.forEach(c => { meta[c.field] = c; });
            setColumnsMeta(meta);
          }
        })
        .catch(() => {});
      fetch(`${B}/table/${tableName}/ref-values`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d?.refs) setRefValues(d.refs); })
        .catch(() => {});
      // Welle 47: verknüpftes Google-Sheet laden
      setSheetInfo(null);
      setSheetPanelOpen(false);
      setSheetUrlInput("");
      fetch(`${B}/table/${tableName}/sheet`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          if (d) {
            setSheetInfo(d);
            setSheetUrlInput(d.sheet?.url || "");
          }
        })
        .catch(() => {});
      // Verknüpfte Tabellen (FKs + Haupt-/Untertabellen) für die Sprung-Buttons
      setVerknuepfungen([]);
      fetch(`${B}/table/${tableName}/verknuepfungen`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d) setVerknuepfungen(d.verknuepfungen || []); })
        .catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setTableLoading(false);
    }
  };

  // === Zeilen-Suche + Spaltenfilter ===
  const getFilteredRows = useCallback(() => {
    if (!tableData) return [];
    let rows = tableData.rows;

    // Globale Suche über alle Spalten
    if (rowSearch.trim()) {
      const q = rowSearch.trim().toLowerCase();
      rows = rows.filter(row =>
        tableData.columns.some(col => String(row[col] ?? "").toLowerCase().includes(q))
      );
    }

    // Welle 123: „nur aktive" — Konten mit „geschlossen am", gekündigte Verträge ausblenden
    const aktivSpalte = AKTIV_SPALTEN.find(c => tableData.columns.includes(c));
    if (aktivSpalte && nurAktive) rows = rows.filter(row => istLeer(row[aktivSpalte]));

    // Spalten-Einzelfilter — „(leer)"/„∅" = nur leere Zellen, „!(leer)"/„!∅" = nur gefüllte
    Object.entries(colFilters).forEach(([col, val]) => {
      const v = val.trim();
      if (!v) return;
      if (LEER_TOKEN.includes(v.toLowerCase())) rows = rows.filter(row => istLeer(row[col]));
      else if (v.startsWith("!") && LEER_TOKEN.includes(v.slice(1).trim().toLowerCase())) rows = rows.filter(row => !istLeer(row[col]));
      else {
        const q = v.toLowerCase();
        rows = rows.filter(row => String(row[col] ?? "").toLowerCase().includes(q));
      }
    });

    // Welle 123: Sortieren per Klick auf die Spaltenüberschrift (Zahlen als Zahl, leere ans Ende)
    if (sortierung) {
      const { col, ab } = sortierung;
      rows = [...rows].sort((a, b) => {
        const x = a[col], y = b[col];
        if (istLeer(x) || istLeer(y)) return istLeer(x) === istLeer(y) ? 0 : (istLeer(x) ? 1 : -1);
        const nx = Number(x), ny = Number(y);
        const r = !Number.isNaN(nx) && !Number.isNaN(ny) ? nx - ny
          : String(x).localeCompare(String(y), "de", { numeric: true, sensitivity: "base" });
        return ab ? -r : r;
      });
    }
    return rows;
  }, [tableData, rowSearch, colFilters, nurAktive, sortierung]);

  // === Inline-Editing ===
  // Welle 46.G: PK aus den echten Spalten-Metadaten (key === 'PRI') — die alte
  // Annahme „erste Spalte = auto-increment-PK" verschluckte bei VARCHAR-PKs
  // (neu_vertraege.ID) die Eingabe kommentarlos.
  const pkColumn =
    Object.values(columnsMeta).find(c => c.key === "PRI")?.field ||
    tableData?.columns?.[0] || "id";
  const pkIsAuto = String(columnsMeta[pkColumn]?.extra || "").includes("auto_increment");

  const startEdit = (rowIdx) => {
    setEditRow(rowIdx);
    setEditingCell(null);
    const rows = getFilteredRows();
    setEditRowPk(rows[rowIdx] ? rows[rowIdx][pkColumn] : null);
  };

  const cancelEdit = () => {
    setEditRow(null);
    setEditingCell(null);
    setEditRowPk(null);
    setSaveMsg(null);
  };

  const isBlob = (val) => typeof val === "string" && val.startsWith("[BLOB:");

  const handleBlobUpload = async (rowIdx, col, file) => {
    if (!file || !selectedTable) return;
    const rows = getFilteredRows();
    const row = rows[rowIdx];
    if (!row) return;
    setBlobUploading({ rowIdx, col });
    setSaveMsg(null);
    try {
      const fd = new FormData();
      fd.append("pk_column", pkColumn);
      fd.append("pk_value", String(row[pkColumn]));
      fd.append("col_name", col);
      fd.append("file", file);
      const resp = await fetch(`${B}/table/${selectedTable}/row/blob`, {
        method: "PUT",
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) throw new Error(data.detail || "Upload fehlgeschlagen");
      const newLabel = `[BLOB: ${data.size_kb} KB]`;
      setTableData(prev => {
        if (!prev) return prev;
        const pkVal = row[pkColumn];
        const updatedRows = prev.rows.map(r =>
          r[pkColumn] === pkVal ? { ...r, [col]: newLabel } : r
        );
        return { ...prev, rows: updatedRows };
      });
      setSaveMsg({ ok: true, text: `✅ Datei hochgeladen (${data.size_kb} KB)` });
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setBlobUploading(null);
    }
  };

  const deleteRow = async (rowIdx) => {
    const rows = getFilteredRows();
    const row = rows[rowIdx];
    if (!row || !selectedTable) return;
    // Original-PK verwenden, falls die PK-Zelle gerade lokal editiert wurde
    const pkVal = editRow === rowIdx && editRowPk !== null ? editRowPk : row[pkColumn];
    if (!window.confirm(`Zeile mit ${pkColumn} = ${pkVal} wirklich löschen?`)) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const resp = await fetch(`${B}/table/${selectedTable}/row`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pk_column: pkColumn, pk_value: pkVal }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) throw new Error(data.detail || "Löschen fehlgeschlagen");
      setTableData(prev => {
        if (!prev) return prev;
        return { ...prev, rows: prev.rows.filter(r => r[pkColumn] !== pkVal) };
      });
      setSaveMsg({ ok: true, text: "🗑️ Zeile gelöscht" });
      setEditRow(null);
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleCellClick = (rowIdx, col) => {
    if (editRow !== rowIdx) return;
    if (col === pkColumn && pkIsAuto) return; // Auto-Nummern nicht editierbar
    setEditingCell({ rowIdx, col });
    const rows = getFilteredRows();
    setEditingValue(String(rows[rowIdx]?.[col] ?? ""));
  };

  const handleCellChange = (e) => setEditingValue(e.target.value);

  const commitCellEdit = (rowIdx, col) => {
    const rows = getFilteredRows();
    const row = rows[rowIdx];
    if (!row) return;
    // Wert lokal in tableData aktualisieren — Match über Objekt-Identität,
    // damit auch der PK selbst editierbar ist (Welle 46.G)
    setTableData(prev => {
      if (!prev) return prev;
      const updatedRows = prev.rows.map(r =>
        r === row ? { ...r, [col]: editingValue } : r
      );
      return { ...prev, rows: updatedRows };
    });
    setEditingCell(null);
  };

  const saveRow = async (rowIdx) => {
    const rows = getFilteredRows();
    const row = rows[rowIdx];
    if (!row || !selectedTable) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      // Welle 46.G: pk_value ist der ORIGINAL-PK vom Edit-Start; eine geänderte
      // PK-Zelle wandert als normale Änderung mit (vorher still verworfen).
      const pkVal = editRowPk !== null ? editRowPk : row[pkColumn];
      const changes = {};
      tableData.columns.forEach(col => {
        if (col !== pkColumn) changes[col] = row[col] === undefined ? null : row[col];
      });
      if (!pkIsAuto && String(row[pkColumn] ?? "") !== String(pkVal ?? "")) {
        if (!String(row[pkColumn] ?? "").trim()) {
          throw new Error(`${pkColumn} darf nicht leer sein.`);
        }
        changes[pkColumn] = row[pkColumn];
      }
      const resp = await fetch(`${B}/table/${selectedTable}/row`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pk_column: pkColumn, pk_value: pkVal, changes }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) throw new Error(data.detail || "Speichern fehlgeschlagen");
      setSaveMsg({ ok: true, text: "✅ Gespeichert" });
      setEditRow(null);
      setEditingCell(null);
      setEditRowPk(null);
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setSaving(false);
    }
  };

  const insertRow = async () => {
    if (!selectedTable) return;
    setInserting(true);
    setSaveMsg(null);
    try {
      // Welle 46.G: VARCHAR-PK ist Pflicht (kein Auto-Wert)
      if (!pkIsAuto && !String(insertData[pkColumn] ?? "").trim()) {
        throw new Error(`${pkColumn} ist ein Pflichtfeld (diese Tabelle vergibt keine automatische Nummer).`);
      }
      // Leere Strings als null senden
      const cleaned = {};
      Object.entries(insertData).forEach(([k, v]) => {
        cleaned[k] = v === "" ? null : v;
      });
      const resp = await fetch(`${B}/table/${selectedTable}/row`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: cleaned }),
      });
      const res = await resp.json();
      if (!resp.ok || !res.ok) throw new Error(res.detail || "Einfügen fehlgeschlagen");
      const neueId = pkIsAuto ? res.insert_id : (insertData[pkColumn] ?? res.insert_id);
      setSaveMsg({ ok: true, text: `✅ Zeile eingefügt (${pkColumn}: ${neueId})` });
      setShowInsert(false);
      setInsertData({});
      // Tabelle neu laden
      await fetchTableData(selectedTable);
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setInserting(false);
    }
  };


  // ── Welle 47: Google-Sheet-Verknüpfung ──
  const reloadSheetInfo = async () => {
    if (!selectedTable) return;
    try {
      const r = await fetch(`${B}/table/${selectedTable}/sheet`);
      if (r.ok) {
        const d = await r.json();
        setSheetInfo(d);
        setSheetUrlInput(d.sheet?.url || "");
      }
      // Welle 49: Fehlerzeilen des letzten Laufs mitladen
      const s = await fetch(`${B}/sheet-sync/status`);
      if (s.ok) {
        const st = await s.json();
        const eigen = (st.tabellen || []).find(t => t.tabelle === selectedTable);
        setSheetFehler(eigen?.fehler || []);
        if (eigen) {
          setSheetInfo(prev => prev ? { ...prev, sheet: { ...(prev.sheet || {}), ...eigen } } : prev);
        }
      }
    } catch {}
  };

  // Welle 49: Auto-Sync je Tabelle ein-/ausschalten und Intervall setzen
  const setSheetConfig = async (aenderung) => {
    try {
      const r = await fetch(`${B}/table/${selectedTable}/sheet-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aenderung),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Speichern fehlgeschlagen");
      await reloadSheetInfo();
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    }
  };

  const saveSheetLink = async () => {
    setSheetBusy(true);
    setSaveMsg(null);
    try {
      const r = await fetch(`${B}/table/${selectedTable}/sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: sheetUrlInput }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Speichern fehlgeschlagen");
      await reloadSheetInfo();
      setSaveMsg({ ok: true, text: "🔗 Google-Sheet verknüpft." });
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setSheetBusy(false);
    }
  };

  const removeSheetLink = async () => {
    if (!window.confirm("Google-Verknüpfung für diese Tabelle lösen? (Das Sheet selbst bleibt bestehen)")) return;
    try {
      await fetch(`${B}/table/${selectedTable}/sheet`, { method: "DELETE" });
      await reloadSheetInfo();
    } catch {}
  };

  const syncFromSheet = async () => {
    setSheetBusy(true);
    setSaveMsg(null);
    try {
      const dryResp = await fetch(`${B}/table/${selectedTable}/sheet-sync?dry_run=1`, { method: "POST" });
      const dry = await dryResp.json();
      if (!dryResp.ok) throw new Error(dry.detail || "Vorschau fehlgeschlagen");
      const zusammen =
        `${dry.neu} neu, ${dry.geaendert} geändert, ${dry.unveraendert} unverändert` +
        (dry.fehler?.length ? `, ${dry.fehler.length} Fehler` : "");
      if (dry.neu + dry.geaendert === 0) {
        setSaveMsg({ ok: true, text: `Google-Sheet: keine Änderungen (${zusammen}).` });
        return;
      }
      const beispiele = (dry.fehler || []).slice(0, 5).map(f => `Zeile ${f.zeile}: ${f.grund}`).join("\n");
      if (!window.confirm(
        `Vorschau Google → ${selectedTable}:\n${zusammen}` +
        (beispiele ? `\n\nFehler (Auszug):\n${beispiele}\n\nFehlerhafte Zeilen werden übersprungen.` : "") +
        `\n\nJetzt übernehmen? (Zeilen werden nie gelöscht)`)) {
        setSaveMsg({ ok: true, text: "Sync abgebrochen." });
        return;
      }
      const resp = await fetch(`${B}/table/${selectedTable}/sheet-sync`, { method: "POST" });
      const res = await resp.json();
      if (!resp.ok) throw new Error(res.detail || "Sync fehlgeschlagen");
      setSaveMsg({ ok: true, text: `✅ Aus Google übernommen: ${res.neu} neu, ${res.geaendert} geändert` +
        (res.fehler?.length ? `, ${res.fehler.length} Fehler übersprungen` : "") + "." });
      await fetchTableData(selectedTable);
      await reloadSheetInfo();
    } catch (e) {
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setSheetBusy(false);
    }
  };

  // Welle 49: In Google öffnen — legt bei Bedarf einen eigenen Tab im
  // Sammel-Sheet an, befüllt ihn und öffnet ihn direkt.
  const openInGoogle = async (neu = false) => {
    setSheetBusy(true);
    setSaveMsg(null);
    // Popup-Blocker: Fenster VOR dem await öffnen und danach umleiten.
    // WICHTIG ohne "noopener" — damit liefert window.open null zurück und
    // wir hätten keine Referenz zum Umleiten.
    const fenster = window.open("about:blank", "_blank");
    if (fenster) fenster.opener = null;
    try {
      const r = await fetch(
        `${B}/table/${selectedTable}/google-oeffnen${neu ? "?neu=1" : ""}`,
        { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Öffnen fehlgeschlagen");
      // Link immer mit anzeigen — blockiert der Browser das Fenster, reicht ein Klick
      setSaveMsg({ ok: true, text: d.hinweis || "Google-Tabelle bereit.", link: d.url });
      await reloadSheetInfo();
      if (d.url) {
        let offen = false;
        try {
          if (fenster && !fenster.closed) { fenster.location = d.url; offen = true; }
        } catch (_) { /* Fenster nicht mehr erreichbar */ }
        if (!offen) window.open(d.url, "_blank", "noopener");
      }
    } catch (e) {
      if (fenster) fenster.close();
      setSaveMsg({ ok: false, text: "❌ " + e.message });
    } finally {
      setSheetBusy(false);
    }
  };

  const fetchExcelCode = async (tableName) => {
    try {
      // Erst Spalten laden falls nicht schon vorhanden
      let columns = [];
      if (tableData && tableData.table_name === tableName) {
        columns = tableData.columns;
      } else {
        const colResp = await fetch(`${B}/table/${tableName}?limit=1`);
        if (colResp.ok) {
          const colData = await colResp.json();
          columns = colData.columns || [];
        }
      }

      const response = await fetch(`${B}/table/${tableName}/excel`);
      if (!response.ok) throw new Error("Fehler beim Abrufen des Excel-Codes");
      const data = await response.json();
      // Welle 104.2: Bei einer Lasche der Daten.xlsm kommen die Excel-Überschriften
      // in Blatt-Reihenfolge vom Server (ohne versteckte Spalten) — genau wie in Google.
      const spalten = (data.spalten && data.spalten.length) ? data.spalten : columns;
      setExcelCode(data);
      setPopupColumns(spalten);
      setSelectedColumns(new Set(spalten));
      setColumnsExpanded(false);
      setCopied(false);
      setShowExcelPopup(true);
    } catch (err) {
      setError(err.message);
    }
  };

  // M-Code mit Spalten-Filter generieren
  const buildFilteredCode = useCallback(() => {
    if (!excelCode) return "";
    const baseCode = excelCode.excel_code;
    const allSelected = selectedColumns.size === popupColumns.length;

    if (allSelected || popupColumns.length === 0) {
      return baseCode;
    }

    // Gewählte Spalten als Power Query Liste formatieren
    const colList = popupColumns
      .filter(c => selectedColumns.has(c))
      .map(c => `"${c}"`)
      .join(", ");

    // Den letzten Schritt („in <Name>") um einen SelectColumns-Schritt erweitern —
    // der Name ist je nach Tabelle „Tabelle" oder „Typen" (Lasche der Daten.xlsm).
    const m = baseCode.match(/\n\s*in\s*\n\s*([A-Za-zÄÖÜäöü_][\w]*)\s*$/);
    if (!m) return baseCode;
    const letzter = m[1];
    return baseCode.slice(0, m.index) +
      `,\n    Gefiltert = Table.SelectColumns(${letzter}, {${colList}})\nin\n    Gefiltert`;
  }, [excelCode, selectedColumns, popupColumns]);

  const toggleColumn = (col) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) {
        // Mindestens 1 Spalte muss bleiben
        if (next.size > 1) next.delete(col);
      } else {
        next.add(col);
      }
      return next;
    });
    setCopied(false);
  };

  const selectAllColumns = () => {
    setSelectedColumns(new Set(popupColumns));
    setCopied(false);
  };

  const selectNoColumns = () => {
    // Mindestens erste Spalte behalten
    if (popupColumns.length > 0) {
      setSelectedColumns(new Set([popupColumns[0]]));
    }
    setCopied(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => alert("❌ Fehler beim Kopieren"));
  };

  return (
    <div className="database-tables-page">
      <h1>{titel}</h1>
      
      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Tabelle suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {mitStruktur && (
        <div className="sync-area">
          <button className="sync-dropbox-btn" onClick={() => setAbgleichOffen(true)}
            title="Die echte Daten.xlsm hochladen und jede Lasche mit der Datenbank vergleichen — übernommen wird nur, was du anhakst">
            📊 Daten.xlsm abgleichen
          </button>
        </div>
      )}
      {abgleichOffen && <ExcelAbgleich B={B} onClose={() => setAbgleichOffen(false)}
        onUebernommen={() => { fetchTables(); if (selectedTable) fetchTableData(selectedTable); }} />}
      {vbaTabelle && <VbaDialog B={B} tabelle={vbaTabelle} onClose={() => setVbaTabelle(null)} />}

      {loading && <p className="loading">Lade Tabellen...</p>}
      {error && <p className="error">❌ {error}</p>}

      {/* === Sync-Bereich === */}
      {mitSync && <div className="sync-area">
        <div
          className={`sync-dropzone${syncDragging ? " dragging" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={handleFileInput}
          />
          {syncLoading
            ? <span className="sync-spinner">⏳ Analyse läuft...</span>
            : <span>📂 .xlsx Datei hierher ziehen oder <strong>klicken</strong></span>
          }
        </div>
        <button
          className="sync-dropbox-btn"
          onClick={fetchDropboxFiles}
          disabled={dropboxLoading}
        >
          {dropboxLoading ? "⏳ Scanne..." : "📦 Dropbox scannen"}
        </button>
      </div>}

      {/* Dropbox-Dateien Liste */}
      {dropboxFiles && dropboxFiles.ok && dropboxFiles.files.length > 0 && (
        <div className="dropbox-file-list">
          <h3>📦 Dropbox: {dropboxFiles.folder}</h3>
          <ul>
            {dropboxFiles.files.map((f, i) => (
              <li key={i}>
                <span className="dbx-file-name">{f.name}</span>
                <span className="dbx-file-size">{(f.size / 1024).toFixed(0)} KB</span>
                <button
                  className="dbx-diff-btn"
                  onClick={() => handleDropboxDiff(f.path)}
                  disabled={syncLoading}
                >
                  🔄 Vergleichen
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dropboxFiles && !dropboxFiles.ok && (
        <p className="error">❌ Dropbox: {dropboxFiles.error}</p>
      )}

      <div className="tables-container">
        {/* Tabellenliste links — gruppiert */}
        <div className="tables-list">
          <h2>Tabellen ({tables.filter(t => t.name.toLowerCase().includes(searchTerm.toLowerCase())).length})</h2>
          {getGroupedTables().map((grp) => (
            <div key={grp.alias} className="table-group">
              <div
                className={"table-group-header" + (dragOverGroup === grp.alias ? " drag-over" : "")}
                onClick={() => toggleGroup(grp.alias)}
                onDragOver={(e) => { e.preventDefault(); setDragOverGroup(grp.alias); }}
                onDragLeave={() => setDragOverGroup(g => (g === grp.alias ? null : g))}
                onDrop={(e) => handleGroupDrop(e, grp.alias)}
              >
                <span className="group-toggle">{collapsedGroups.has(grp.alias) ? "▶" : "▼"}</span>
                <span className="group-alias">{grp.alias}</span>
                <span className="group-label">{grp.label}</span>
                <span className="group-count">{grp.tables.length}</span>
              </div>
              {!collapsedGroups.has(grp.alias) && (
                <ul>
                  {grp.tables.map((table) => (
                    <li
                      key={table.name}
                      className={selectedTable === table.name ? "selected" : ""}
                      draggable
                      onDragStart={(e) => handleTableDragStart(e, table.name)}
                      title="Ziehen, um die Tabelle in eine andere Sektion zu verschieben"
                    >
                      {table.kinder?.length > 0 ? (
                        <button
                          className="subtable-toggle"
                          onClick={(e) => { e.stopPropagation(); toggleParent(table.name); }}
                          title={`${table.kinder.length} Untertabellen`}
                        >
                          {(expandedParents.has(table.name) || searchTerm) ? "▼" : "▶"}
                        </button>
                      ) : !mitSync ? (
                        <span className="writeable-toggle" title={table.ist_view ? "Ansicht – nur lesen" : "Tabelle"}
                              style={{ cursor: "default" }}>{table.ist_view ? "👁" : "▦"}</span>
                      ) : (
                        <button
                          className={`writeable-toggle ${syncConfigs[table.name]?.writeable ? "on" : ""}`}
                          onClick={(e) => { e.stopPropagation(); toggleWriteable(table.name); }}
                          title={syncConfigs[table.name]?.writeable ? "Sync aktiv – klicken zum Sperren" : "Sync gesperrt – klicken zum Freigeben"}
                        >
                          {syncConfigs[table.name]?.writeable ? "🔓" : "🔒"}
                        </button>
                      )}
                      <span
                        className="table-name"
                        onClick={() => fetchTableData(table.name)}
                      >
                        {table.name}
                      </span>
                      {table.kinder?.length > 0 && (
                        <span className="subtable-count" title={`${table.kinder.length} Untertabellen`}>
                          +{table.kinder.length}
                        </span>
                      )}
                      <span className="row-count">{table.row_count}</span>
                      <button
                        className="excel-btn"
                        onClick={() => fetchExcelCode(table.name)}
                        title="Excel-Code anzeigen"
                      >
                        📋
                      </button>
                      {mitStruktur && table.group === "XLS" && !table.ist_view && (
                        <button className="excel-btn" onClick={(e) => { e.stopPropagation(); setVbaTabelle(table.name); }}
                          title="Excel-Modul (VBA) herunterladen — Knöpfe Daten ziehen / hochladen / live">⬇</button>
                      )}
                    </li>
                  )).reduce((acc, li, i) => {
                    const table = grp.tables[i];
                    acc.push(li);
                    if (table.kinder?.length > 0 && (expandedParents.has(table.name) || searchTerm)) {
                      for (const kind of table.kinder) {
                        acc.push(
                          <li
                            key={kind.name}
                            className={"subtable-row" + (selectedTable === kind.name ? " selected" : "")}
                          >
                            <span className="subtable-indent">└</span>
                            <span className="table-name" onClick={() => fetchTableData(kind.name)}>
                              {kind.name}
                            </span>
                            <span className="row-count">{kind.row_count}</span>
                            <button className="excel-btn" onClick={() => fetchExcelCode(kind.name)} title="Excel-Code anzeigen">
                              📋
                            </button>
                          </li>
                        );
                      }
                    }
                    return acc;
                  }, [])}
                </ul>
              )}
            </div>
          ))}

          {/* Welle 21.D: neue Sektion anlegen */}
          {newSectionOpen ? (
            <div className="new-section-form">
              <input
                value={newSectionAlias}
                onChange={(e) => setNewSectionAlias(e.target.value)}
                placeholder="Kürzel (z. B. ABC)"
                maxLength={10}
                autoFocus
              />
              <input
                value={newSectionLabel}
                onChange={(e) => setNewSectionLabel(e.target.value)}
                placeholder="Name der Sektion"
                onKeyDown={(e) => { if (e.key === "Enter") createSection(); }}
              />
              <button className="new-section-save" onClick={createSection} title="Sektion anlegen">✓</button>
              <button className="new-section-cancel" onClick={() => { setNewSectionOpen(false); setNewSectionAlias(""); setNewSectionLabel(""); }} title="Abbrechen">✕</button>
            </div>
          ) : (
            <button className="new-section-btn" onClick={() => setNewSectionOpen(true)}>
              + Neue Sektion
            </button>
          )}
          {mitStruktur && <StrukturSeitenleiste B={B} onGeaendert={() => { fetchTables(); if (selectedTable) fetchTableData(selectedTable); }} />}
        </div>

        {/* Tabelleninhalt rechts */}
        <div className="table-preview">
          {tableLoading && <p className="loading">Lade Daten...</p>}

          {!tableLoading && selectedTable && tableData && (() => {
            const filtered = getFilteredRows();
            const istView = !!tables.find(t => t.name === selectedTable)?.ist_view;
            // Gruppen in Spaltenreihenfolge zu Blöcken zusammenfassen
            const gruppeVon = (c) => columnsMeta[c]?.gruppe || "";
            const hatGruppen = tableData.columns.some(c => gruppeVon(c));
            const bloecke = [];
            tableData.columns.forEach((c) => {
              const g = gruppeVon(c);
              const letzter = bloecke[bloecke.length - 1];
              if (letzter && letzter.gruppe === g) letzter.spalten.push(c);
              else bloecke.push({ gruppe: g, spalten: [c], key: `${g}#${bloecke.length}` });
            });
            const gruppenNamen = [...new Set(bloecke.map(b => b.gruppe).filter(Boolean))];
            const farbe = (g) => GRUPPEN_FARBEN[Math.max(0, gruppenNamen.indexOf(g)) % GRUPPEN_FARBEN.length];
            // eingeklappte Gruppe: nur ihre erste Spalte bleibt als schmaler Platzhalter sichtbar
            const sichtbareSpalten = bloecke.flatMap(b => (b.gruppe && zuGruppen.has(b.gruppe)) ? [] : b.spalten);
            const toggleGruppe = (g) => setZuGruppen(prev => { const n = new Set(prev); if (n.has(g)) n.delete(g); else n.add(g); return n; });
            const PREVIEW_LIMIT = 100;
            const visibleRows = showAllRows ? filtered : filtered.slice(0, PREVIEW_LIMIT);

            return (
              <>
                <datalist id="db-filter-leer">
                  <option value="(leer)">nur leere Zellen</option>
                  <option value="!(leer)">nur gefüllte Zellen</option>
                </datalist>
                <div className="preview-header">
                  <h2>{selectedTable}</h2>
                  {AKTIV_SPALTEN.some(c => tableData.columns.includes(c)) && (
                    <label className="db-nur-aktive" title={`Zeilen mit „${AKTIV_SPALTEN.find(c => tableData.columns.includes(c))}“ ausblenden (geschlossen / gekündigt)`}>
                      <input type="checkbox" checked={nurAktive} onChange={e => { setNurAktive(e.target.checked); setShowAllRows(false); }} /> nur aktive
                    </label>
                  )}
                  <span className="info">
                    {filtered.length < tableData.total
                      ? `${filtered.length} gefiltert / ${tableData.total} gesamt`
                      : `${tableData.total} Zeilen`}
                  </span>
                  <button className="excel-button" onClick={() => fetchExcelCode(selectedTable)}>
                    📥 Excel-Code
                  </button>
                  {/* Welle 49: Ein Klick — Tabelle in Google öffnen und dort bearbeiten.
                      (Der Excel-Umweg ist raus — die Pflege läuft über Google.
                      Die Endpoints export-excel/import-excel bleiben serverseitig bestehen.) */}
                  <button
                    className="excel-button"
                    disabled={sheetBusy}
                    onClick={() => openInGoogle(false)}
                    title="Diese Tabelle in Google Tabellen öffnen (legt bei Bedarf einen eigenen Tab an)"
                  >
                    {sheetBusy ? "⏳ Google…" : "🌐 In Google öffnen"}
                  </button>
                  <button
                    className="excel-button"
                    onClick={() => setSheetPanelOpen(v => !v)}
                    title="Google-Verknüpfung, Auto-Sync und Fehlerliste"
                  >
                    {sheetInfo?.verknuepft ? "🔗 Google ✓" : "🔗 Google"}
                  </button>
                  {mitStruktur && !istView && tables.find(t => t.name === selectedTable)?.group === "XLS" && (
                    <button className="excel-button" onClick={() => setVbaTabelle(selectedTable)}
                      title="Excel-Modul (VBA): Daten ziehen / hochladen / live">⬇ VBA</button>
                  )}
                  {mitStruktur && !istView && (
                    <>
                      <button className="excel-button" onClick={() => setSpaltenPanel(v => !v)}
                              title="Spalten hinzufügen, umbenennen, löschen">
                        🧱 Spalten
                      </button>
                      <button className="excel-button" title="Tabelle entfernen (wird nur umbenannt, Daten bleiben liegen)"
                              onClick={async () => {
                                if (!window.confirm(`Tabelle „${selectedTable}" entfernen?\n\nSie wird nur umbenannt (_geloescht_…) — die Daten bleiben in der Datenbank liegen.`)) return;
                                try { await tabelleLoeschen(B, selectedTable); setSelectedTable(null); setTableData(null); fetchTables(); }
                                catch (e) { alert("Nicht möglich: " + e.message); }
                              }}>
                        🗑 Tabelle
                      </button>
                    </>
                  )}
                  {!istView && <button
                    className="insert-row-btn"
                    onClick={() => {
                      setShowInsert(v => !v);
                      setInsertData({});
                      setSaveMsg(null);
                    }}
                    title="Neue Zeile einfügen"
                  >
                    {showInsert ? "✕ Abbrechen" : "➕ Neue Zeile"}
                  </button>}
                </div>
                {istView && (
                  <div className="save-msg ok">👁 Ansicht — wird aus den Tabellen berechnet und lässt sich hier nur lesen (und nach Google holen).</div>
                )}
                {mitStruktur && spaltenPanel && !istView && (
                  <SpaltenPanel B={B} tabelle={selectedTable} spaltenMeta={Object.values(columnsMeta)}
                                onSchliessen={() => setSpaltenPanel(false)}
                                onGeaendert={() => { fetchTableData(selectedTable); fetchTables(); }} />
                )}

                {/* Welle 47: Google-Sheet-Panel */}
                {sheetPanelOpen && (
                  <div className="insert-panel" style={{ marginBottom: 10 }}>
                    <div className="insert-panel-title">🔗 Google-Sheet für {selectedTable}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <input
                        type="text"
                        className="insert-field-input"
                        style={{ flex: 2, minWidth: 280 }}
                        placeholder="https://docs.google.com/spreadsheets/… (Tab über #gid=… im Link)"
                        value={sheetUrlInput}
                        onChange={e => setSheetUrlInput(e.target.value)}
                      />
                      <button className="insert-save-btn" disabled={sheetBusy || !sheetUrlInput.trim()}
                              onClick={saveSheetLink}>💾 Verknüpfen</button>
                      <button className="insert-save-btn" disabled={sheetBusy || !sheetInfo?.verknuepft}
                              onClick={syncFromSheet}
                              title="Sheet lesen und per Schlüssel in die Tabelle übernehmen (mit Vorschau)">
                        {sheetBusy ? "⏳…" : "⟳ Aus Google übernehmen"}
                      </button>
                      <button className="insert-save-btn" disabled={sheetBusy}
                              onClick={() => openInGoogle(false)}
                              title="Google-Tabelle öffnen (legt bei Bedarf einen Tab im Sammel-Sheet an)">
                        🌐 In Google öffnen
                      </button>
                      <button className="insert-save-btn" disabled={sheetBusy}
                              onClick={() => {
                                if (window.confirm(
                                  `Für „${selectedTable}" einen frischen Google-Tab anlegen?\n\n` +
                                  `Eine bisherige Verknüpfung wird ersetzt, die aktuellen Daten aus der ` +
                                  `Datenbank werden hineingeschrieben.`)) openInGoogle(true);
                              }}
                              title="Frischen Tab anlegen und mit den aktuellen DB-Daten füllen">
                        ➕ Neuer Google-Tab
                      </button>
                      {sheetInfo?.verknuepft && (
                        <button className="cancel-edit-btn" onClick={removeSheetLink}
                                title="Verknüpfung lösen">🗑</button>
                      )}
                    </div>

                    {/* Welle 49: Auto-Sync steuern */}
                    {sheetInfo?.verknuepft && (
                      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
                                    marginTop: 8, fontSize: 12 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6 }}
                               title="Holt Änderungen aus Google automatisch in die Datenbank">
                          <input type="checkbox"
                                 checked={sheetInfo.sheet?.auto_sync !== 0 && sheetInfo.sheet?.auto_sync !== false}
                                 onChange={e => setSheetConfig({ auto_sync: e.target.checked })} />
                          Automatisch übernehmen
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          alle
                          <input type="number" min="5" max="1440" style={{ width: 70 }}
                                 value={sheetInfo.sheet?.interval_minutes ?? 15}
                                 onChange={e => setSheetConfig({ interval_minutes: e.target.value })} />
                          Minuten
                        </label>
                        {sheetInfo.sheet?.laeuft_gerade && <span className="badge">⏳ läuft gerade</span>}
                      </div>
                    )}

                    {sheetInfo?.sheet && (
                      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>
                        {sheetInfo.sheet.beschreibung ? `${sheetInfo.sheet.beschreibung} — ` : ""}
                        {sheetInfo.sheet.last_sync
                          ? `zuletzt übernommen ${sheetInfo.sheet.last_sync}${sheetInfo.sheet.last_status ? ` (${sheetInfo.sheet.last_status})` : ""}`
                          : "noch nie übernommen"}
                      </div>
                    )}

                    {/* Welle 49: übersprungene Zeilen mit Grund */}
                    {sheetFehler.length > 0 && (
                      <div style={{ marginTop: 8, padding: "6px 8px", borderRadius: 4,
                                    background: "rgba(207,34,46,0.08)", fontSize: 11.5 }}>
                        <b>{sheetFehler.length} Zeile(n) übersprungen — bitte in Google korrigieren:</b>
                        <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                          {sheetFehler.slice(0, 8).map((f, i) => (
                            <li key={i}>Zeile {f.zeile}: {f.grund}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>
                      Kopfzeile = Spaltennamen (tolerant, deutsche Datums-/Betragsformate ok) ·
                      Zeilen werden beim Übernehmen nie gelöscht · in Google bearbeiten, die App holt es ab.
                    </div>
                  </div>
                )}

                {/* Globale Suche */}
                <div className="row-search-bar">
                  <input
                    type="text"
                    placeholder="🔍 Alle Spalten durchsuchen..."
                    value={rowSearch}
                    onChange={e => { setRowSearch(e.target.value); setShowAllRows(false); }}
                  />
                  {rowSearch && (
                    <button className="clear-search-btn" onClick={() => setRowSearch("")}>✕</button>
                  )}
                </div>

                {/* Welle 46.G: Datalists mit gültigen Referenz-Werten (ID — Label) */}
                {Object.entries(refValues).map(([col, ref]) => (
                  <datalist key={col} id={`ref-${selectedTable}-${col}`}>
                    {ref.werte.map(w => (
                      <option key={w.id} value={w.id}>{w.label ? `${w.id} — ${w.label}` : w.id}</option>
                    ))}
                  </datalist>
                ))}

                {/* Insert-Panel */}
                {showInsert && tableData && (
                  <div className="insert-panel">
                    <div className="insert-panel-title">➕ Neue Zeile einfügen</div>
                    <div className="insert-fields">
                      {/* Welle 46.G: PK-Feld anzeigen, wenn die Tabelle keine
                          Auto-Nummer vergibt (z. B. neu_vertraege.ID) */}
                      {tableData.columns.filter(col => col !== pkColumn || !pkIsAuto).map(col => (
                        <label key={col} className="insert-field">
                          <span className="insert-field-label">
                            {col === pkColumn ? `🔑 ${col} (Pflicht)` : col}
                            {refValues[col] ? ` → ${refValues[col].ziel}` : ""}
                          </span>
                          <input
                            type="text"
                            className="insert-field-input"
                            placeholder={refValues[col] ? `aus ${refValues[col].ziel} wählen…` : col}
                            list={refValues[col] ? `ref-${selectedTable}-${col}` : undefined}
                            value={insertData[col] ?? ""}
                            onChange={e => setInsertData(prev => ({ ...prev, [col]: e.target.value }))}
                          />
                        </label>
                      ))}
                    </div>
                    <div className="insert-panel-actions">
                      <button
                        className="insert-save-btn"
                        onClick={insertRow}
                        disabled={inserting}
                      >
                        {inserting ? "⏳ Einfügen..." : "💾 Einfügen"}
                      </button>
                      <button
                        className="cancel-edit-btn"
                        onClick={() => { setShowInsert(false); setInsertData({}); }}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}

                {/* Speichern-Meldung */}
                {saveMsg && (
                  <div className={`save-msg ${saveMsg.ok ? "ok" : "err"}`}>
                    {saveMsg.text}
                    {saveMsg.link && (
                      <> · <a href={saveMsg.link} target="_blank" rel="noopener noreferrer"
                              style={{ color: "inherit", fontWeight: 700 }}>Google-Tabelle öffnen ↗</a></>
                    )}
                  </div>
                )}

                {hatGruppen && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", margin: "6px 0", fontSize: "0.8rem" }}>
                    <span style={{ color: "var(--color-gray-600)" }}>Spaltengruppen:</span>
                    {gruppenNamen.map(g => (
                      <button key={g} onClick={() => toggleGruppe(g)} title={zuGruppen.has(g) ? "einblenden" : "ausblenden"}
                              style={{ padding: "2px 10px", margin: 0, borderRadius: 12, cursor: "pointer", fontSize: "0.76rem", fontWeight: 700,
                                       border: `1.5px solid ${farbe(g)}`, background: zuGruppen.has(g) ? "transparent" : farbe(g),
                                       color: zuGruppen.has(g) ? farbe(g) : "#fff" }}>
                        {zuGruppen.has(g) ? "▸" : "▾"} {g}
                      </button>
                    ))}
                    <button onClick={() => setZuGruppen(new Set())} style={{ padding: "2px 8px", margin: 0, fontSize: "0.74rem", cursor: "pointer" }}>alle zeigen</button>
                    <button onClick={() => setZuGruppen(new Set(gruppenNamen.filter(g => !["Vertrag", "Liquidität"].includes(g))))}
                            style={{ padding: "2px 8px", margin: 0, fontSize: "0.74rem", cursor: "pointer" }}>nur Vertrag + Liquidität</button>
                  </div>
                )}
                <div className="table-wrapper">
                  <table>
                    <thead>
                      {hatGruppen && (
                        <tr className="col-gruppen-row">
                          <th className="edit-col-header"></th>
                          {bloecke.map(b => {
                            const zu = b.gruppe && zuGruppen.has(b.gruppe);
                            if (zu) return null;
                            return (
                              <th key={b.key} colSpan={b.spalten.length}
                                  onClick={() => b.gruppe && toggleGruppe(b.gruppe)}
                                  title={b.gruppe ? "Klick: Gruppe einklappen" : ""}
                                  style={{ background: b.gruppe ? farbe(b.gruppe) : "transparent", color: "#fff", cursor: b.gruppe ? "pointer" : "default",
                                           textAlign: "left", fontSize: "0.78rem", letterSpacing: "0.04em", padding: "4px 10px",
                                           borderRight: "2px solid var(--bg-card)" }}>
                                {b.gruppe ? `▾ ${b.gruppe} · ${b.spalten.length}` : ""}
                              </th>
                            );
                          })}
                        </tr>
                      )}
                      {/* Spaltenfilter-Zeile (Welle 123: Vorschläge „(leer)" / „!(leer)") */}
                      <tr className="col-filter-row">
                        <th className="edit-col-header"></th>
                        {sichtbareSpalten.map(col => (
                          <th key={col}>
                            <input
                              className="col-filter-input"
                              type="text"
                              placeholder="Filter..."
                              list="db-filter-leer"
                              title="Text = enthält · (leer) = nur leere Zellen · !(leer) = nur gefüllte"
                              value={colFilters[col] || ""}
                              onChange={e => {
                                setColFilters(prev => ({ ...prev, [col]: e.target.value }));
                                setShowAllRows(false);
                              }}
                            />
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <th className="edit-col-header"></th>
                        {sichtbareSpalten.map(col => (
                          <th key={col} title={`${columnsMeta[col]?.kommentar || ""}${columnsMeta[col]?.kommentar ? "\n" : ""}Klick: sortieren`}
                              onClick={() => setSortierung(s => (s?.col !== col ? { col, ab: false } : (s.ab ? null : { col, ab: true })))}
                              style={{ cursor: "pointer", ...(hatGruppen && gruppeVon(col) ? { borderTop: `3px solid ${farbe(gruppeVon(col))}` } : {}) }}>
                            {col}{sortierung?.col === col ? (sortierung.ab ? " ▼" : " ▲") : ""}
                            {/* Welle 102: kurzer Kommentar = Überschrift wie im Excel-Blatt (Lasche Objekte) */}
                            {mitStruktur && columnsMeta[col]?.kommentar && columnsMeta[col].kommentar.length <= 40 && (
                              <div className="col-excel-name">{columnsMeta[col].kommentar.replace(/\n/g, " ")}</div>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row, idx) => {
                        const isEditing = editRow === idx;
                        return (
                          <tr key={idx} className={isEditing ? "editing-row" : ""}>
                            <td className="edit-col">
                              {isEditing ? (
                                <>
                                  <button
                                    className="save-row-btn"
                                    onClick={() => saveRow(idx)}
                                    disabled={saving}
                                    title="Speichern"
                                  >💾</button>
                                  <button
                                    className="cancel-edit-btn"
                                    onClick={cancelEdit}
                                    title="Abbrechen"
                                  >✕</button>
                                  <button
                                    className="delete-row-btn"
                                    onClick={() => deleteRow(idx)}
                                    disabled={saving}
                                    title="Zeile löschen"
                                  >🗑️</button>
                                </>
                              ) : (
                                <button
                                  className="edit-row-btn"
                                  onClick={() => startEdit(idx)}
                                  title="Zeile bearbeiten"
                                >✏️</button>
                              )}
                            </td>
                            {sichtbareSpalten.map(col => {
                              const isCellEditing = isEditing && editingCell?.rowIdx === idx && editingCell?.col === col;
                              const val = row[col] == null ? "" : (typeof row[col] === "object" ? JSON.stringify(row[col]) : String(row[col]));
                              const isBlobCol = isBlob(val);
                              const isBlobUploading = blobUploading?.rowIdx === idx && blobUploading?.col === col;
                              return (
                                <td
                                  key={col}
                                  className={isEditing ? (isBlobCol ? "editable-cell blob-cell" : "editable-cell") : ""}
                                  onClick={() => !isBlobCol && handleCellClick(idx, col)}
                                  title={isEditing && !isBlobCol ? "Klicken zum Bearbeiten" : ""}
                                >
                                  {isEditing && isBlobCol ? (
                                    <label className="blob-upload-label" title="Datei hochladen">
                                      {isBlobUploading ? (
                                        <span className="blob-uploading">⏳ Hochladen…</span>
                                      ) : (
                                        <>
                                          <span className="blob-current-size">{val}</span>
                                          <span className="blob-upload-btn">📁 Datei wählen</span>
                                          <input
                                            type="file"
                                            className="blob-file-input"
                                            onChange={e => {
                                              if (e.target.files[0]) handleBlobUpload(idx, col, e.target.files[0]);
                                            }}
                                          />
                                        </>
                                      )}
                                    </label>
                                  ) : isCellEditing ? (
                                    <input
                                      className="cell-edit-input"
                                      autoFocus
                                      list={refValues[col] ? `ref-${selectedTable}-${col}` : undefined}
                                      value={editingValue}
                                      onChange={handleCellChange}
                                      onBlur={() => commitCellEdit(idx, col)}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") commitCellEdit(idx, col);
                                        if (e.key === "Escape") setEditingCell(null);
                                      }}
                                    />
                                  ) : val}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Erweitern-Button */}
                {!showAllRows && filtered.length > PREVIEW_LIMIT && (
                  <div className="show-more-bar">
                    <span className="show-more-info">
                      {PREVIEW_LIMIT} von {filtered.length} Zeilen angezeigt
                    </span>
                    <button className="show-more-btn" onClick={() => setShowAllRows(true)}>
                      ▼ Alle {filtered.length} Zeilen anzeigen
                    </button>
                  </div>
                )}
                {showAllRows && filtered.length > PREVIEW_LIMIT && (
                  <div className="show-more-bar">
                    <button className="show-more-btn collapse" onClick={() => setShowAllRows(false)}>
                      ▲ Auf {PREVIEW_LIMIT} Zeilen reduzieren
                    </button>
                  </div>
                )}

                {/* Welle 22.F: verknüpfte Tabellen zum Hin-Springen */}
                {verknuepfungen.length > 0 && (
                  <div className="linked-tables-bar">
                    <span className="linked-tables-title">🔗 Verknüpfte Tabellen:</span>
                    {verknuepfungen.map(v => (
                      <button
                        key={v.tabelle}
                        className={"linked-table-btn" +
                          (v.beziehung === "Untertabelle" ? " sub" :
                           v.beziehung === "Haupttabelle" ? " main" : "")}
                        title={v.beziehung}
                        onClick={() => fetchTableData(v.tabelle)}
                      >
                        {v.tabelle}
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}

          {!tableLoading && !selectedTable && (
            <p className="placeholder">👈 Wähle eine Tabelle aus der Liste</p>
          )}
        </div>
      </div>

      {/* Excel-Code Popup */}
      {showExcelPopup && excelCode && (
        <div className="popup-overlay" onClick={() => setShowExcelPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>📊 Excel Power Query Code</h2>
            <h3>Tabelle: {excelCode.table_name}</h3>
            
            <div className="instructions">
              <p><strong>Anleitung:</strong></p>
              <ol>
                <li>Öffne Excel</li>
                <li>Gehe zu <strong>Daten → Daten abrufen → Aus anderen Quellen → Leere Abfrage</strong></li>
                <li>Klicke auf <strong>Erweiterter Editor</strong></li>
                <li>Füge den folgenden Code ein:</li>
              </ol>
            </div>

            {/* Spalten-Filter */}
            {popupColumns.length > 0 && (
              <div className="column-filter">
                <div
                  className="column-filter-header"
                  onClick={() => setColumnsExpanded(prev => !prev)}
                >
                  <span className="column-filter-arrow">{columnsExpanded ? "▼" : "▶"}</span>
                  <span className="column-filter-title">
                    ⚙️ Spalten auswählen ({selectedColumns.size} von {popupColumns.length})
                  </span>
                  {selectedColumns.size < popupColumns.length && (
                    <span className="column-filter-badge">gefiltert</span>
                  )}
                </div>
                {columnsExpanded && (
                  <div className="column-filter-body">
                    <div className="column-filter-actions">
                      <button className="link-btn" onClick={selectAllColumns}>Alle</button>
                      <span className="divider">|</span>
                      <button className="link-btn" onClick={selectNoColumns}>Keine</button>
                    </div>
                    <div className="column-checkboxes">
                      {popupColumns.map(col => (
                        <label key={col} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedColumns.has(col)}
                            onChange={() => toggleColumn(col)}
                          />
                          <span className="col-name">{col}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <pre className="code-block">{buildFilteredCode()}</pre>
            
            <div className="popup-buttons">
              <button 
                className={`copy-btn${copied ? " copied" : ""}`}
                onClick={() => copyToClipboard(buildFilteredCode())}
              >
                {copied ? "✅ Kopiert!" : "📋 Code kopieren"}
              </button>
              <button 
                className="close-btn"
                onClick={() => setShowExcelPopup(false)}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Diff-Modal === */}
      {showDiffModal && syncResult && (
        <div className="popup-overlay" onClick={() => setShowDiffModal(false)}>
          <div className="diff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="diff-modal-header">
              <h2>🔄 Sync-Vergleich: {syncResult.file_name}</h2>
              <button className="close-btn" onClick={() => setShowDiffModal(false)}>✕</button>
            </div>

            {/* Apply-Result Banner */}
            {applyResult && (
              <div className="apply-result-banner">
                {applyResult.results?.map((r, i) => (
                  <div key={i} className={`apply-result-item ${r.ok ? "ok" : "err"}`}>
                    <strong>{r.table_name}:</strong>{" "}
                    {r.ok
                      ? `✅ ${r.inserted || 0} eingefügt, ${r.updated || 0} aktualisiert, ${r.deleted || 0} gelöscht`
                      : `❌ ${r.error}`}
                    {r.errors?.length > 0 && <span className="apply-errors"> ⚠️ {r.errors.length} Fehler</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            {syncResult.tables.length > 1 && (
              <div className="diff-tabs">
                {syncResult.tables.map((t, idx) => (
                  <button
                    key={idx}
                    className={`diff-tab ${diffActiveTab === idx ? "active" : ""}`}
                    onClick={() => setDiffActiveTab(idx)}
                  >
                    {t.db_table || t.sheet_name}
                    {t.diff && (t.diff.added.length + t.diff.modified.length + t.diff.deleted.length) > 0 && (
                      <span className="diff-tab-badge">
                        {t.diff.added.length + t.diff.modified.length + t.diff.deleted.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Diff Content */}
            {syncResult.tables.map((t, tabIdx) => {
              if (tabIdx !== diffActiveTab) return null;
              const ch = diffChecked[tabIdx] || { added: new Set(), modified: new Set(), deleted: new Set() };

              if (t.error) return <div className="diff-error" key={tabIdx}>❌ {t.error}</div>;
              if (!t.diff) return <div className="diff-empty" key={tabIdx}>Keine Zuordnung möglich</div>;

              const { added, modified, deleted, unchanged } = t.diff;
              const hasChanges = added.length + modified.length + deleted.length > 0;

              return (
                <div className="diff-content" key={tabIdx}>
                  <div className="diff-summary">
                    <span className="diff-info">📊 {t.db_table} — {t.match_method}</span>
                    <span className="diff-stat added-stat">+{added.length} neu</span>
                    <span className="diff-stat mod-stat">~{modified.length} geändert</span>
                    <span className="diff-stat del-stat">-{deleted.length} gelöscht</span>
                    <span className="diff-stat ok-stat">={unchanged} gleich</span>
                    {!t.writeable && (
                      <button
                        className="diff-unlock-btn"
                        onClick={async () => {
                          await toggleWriteable(t.db_table);
                          // Update writeable im syncResult
                          setSyncResult(prev => {
                            if (!prev?.tables) return prev;
                            const updated = { ...prev, tables: prev.tables.map(tt =>
                              tt.db_table === t.db_table ? { ...tt, writeable: true } : tt
                            )};
                            return updated;
                          });
                        }}
                      >🔒 Nicht freigegeben — klicken zum Freischalten</button>
                    )}
                  </div>

                  {!hasChanges && <div className="diff-no-changes">✅ Keine Unterschiede</div>}

                  {/* Added */}
                  {added.length > 0 && (
                    <div className="diff-section">
                      <h4 className="diff-section-title added">➕ Neue Zeilen ({added.length})</h4>
                      <div className="diff-table-wrap">
                        <table className="diff-table">
                          <thead>
                            <tr>
                              <th className="diff-check-col">
                                <input
                                  type="checkbox"
                                  checked={ch.added.size === added.length}
                                  onChange={() => {
                                    const allChecked = ch.added.size === added.length;
                                    setDiffChecked(prev => ({
                                      ...prev,
                                      [tabIdx]: {
                                        ...prev[tabIdx],
                                        added: allChecked ? new Set() : new Set(added.map((_, i) => i)),
                                      },
                                    }));
                                  }}
                                  disabled={!t.writeable}
                                />
                              </th>
                              {t.headers?.map(h => <th key={h}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {added.map((row, i) => (
                              <tr key={i} className="diff-row-added">
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={ch.added.has(i)}
                                    onChange={() => toggleDiffRow(tabIdx, "added", i)}
                                    disabled={!t.writeable}
                                  />
                                </td>
                                {t.headers?.map(h => <td key={h}>{String(row.data?.[h] ?? "")}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Modified */}
                  {modified.length > 0 && (
                    <div className="diff-section">
                      <h4 className="diff-section-title modified">✏️ Geänderte Zeilen ({modified.length})</h4>
                      <div className="diff-table-wrap">
                        <table className="diff-table">
                          <thead>
                            <tr>
                              <th className="diff-check-col">
                                <input
                                  type="checkbox"
                                  checked={ch.modified.size === modified.length}
                                  onChange={() => {
                                    const allChecked = ch.modified.size === modified.length;
                                    setDiffChecked(prev => ({
                                      ...prev,
                                      [tabIdx]: {
                                        ...prev[tabIdx],
                                        modified: allChecked ? new Set() : new Set(modified.map((_, i) => i)),
                                      },
                                    }));
                                  }}
                                  disabled={!t.writeable}
                                />
                              </th>
                              <th>PK</th>
                              <th>Spalte</th>
                              <th>Alt (DB)</th>
                              <th>Neu (Excel)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modified.map((mod, i) => (
                              <React.Fragment key={i}>
                                {mod.changes.map((ch2, j) => (
                                  <tr key={`${i}-${j}`} className="diff-row-modified">
                                    {j === 0 && (
                                      <td rowSpan={mod.changes.length}>
                                        <input
                                          type="checkbox"
                                          checked={ch.modified.has(i)}
                                          onChange={() => toggleDiffRow(tabIdx, "modified", i)}
                                          disabled={!t.writeable}
                                        />
                                      </td>
                                    )}
                                    {j === 0 && <td rowSpan={mod.changes.length} className="diff-pk">{mod.pk}</td>}
                                    <td className="diff-col-name">{ch2.col}</td>
                                    <td className="diff-old">{ch2.old}</td>
                                    <td className="diff-new">{ch2.new}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Deleted */}
                  {deleted.length > 0 && (
                    <div className="diff-section">
                      <h4 className="diff-section-title deleted">🗑️ Gelöschte Zeilen ({deleted.length})</h4>
                      <p className="diff-delete-warning">⚠️ Zeilen die in der DB existieren aber nicht in der Excel-Datei. Löschungen sind standardmäßig deaktiviert.</p>
                      <div className="diff-table-wrap">
                        <table className="diff-table">
                          <thead>
                            <tr>
                              <th className="diff-check-col">
                                <input
                                  type="checkbox"
                                  checked={ch.deleted.size === deleted.length && deleted.length > 0}
                                  onChange={() => {
                                    const allChecked = ch.deleted.size === deleted.length;
                                    setDiffChecked(prev => ({
                                      ...prev,
                                      [tabIdx]: {
                                        ...prev[tabIdx],
                                        deleted: allChecked ? new Set() : new Set(deleted.map((_, i) => i)),
                                      },
                                    }));
                                  }}
                                  disabled={!t.writeable}
                                />
                              </th>
                              {t.headers?.map(h => <th key={h}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {deleted.map((row, i) => (
                              <tr key={i} className="diff-row-deleted">
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={ch.deleted.has(i)}
                                    onChange={() => toggleDiffRow(tabIdx, "deleted", i)}
                                    disabled={!t.writeable}
                                  />
                                </td>
                                {t.headers?.map(h => <td key={h}>{String(row.data?.[h] ?? "")}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Footer */}
            <div className="diff-modal-footer">
              <button
                className="diff-apply-btn"
                onClick={handleApply}
                disabled={applyLoading || !syncResult.tables.some(t => t.writeable && t.diff)}
              >
                {applyLoading ? "⏳ Anwenden..." : "✅ Ausgewählte Änderungen anwenden"}
              </button>
              <button className="close-btn" onClick={() => setShowDiffModal(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseTablesPage;
